/**
 * Module-level store for the feedback draft, view state and position.
 *
 * It is a module store (read with `useSyncExternalStore`) rather than a React provider so that the
 * TopBar flag and the shell-scope host share one state without a new provider in the tree, and so
 * TopBar tests that render without any provider keep working. Nothing here is persisted except the
 * corner (see `panelPosition.ts`); the draft is deliberately lost on reload (spec section 4).
 */
import { useSyncExternalStore } from 'react';
import type { CaptureResult } from '@/lib/feedback';
import {
  DEFAULT_CORNER,
  type Corner,
  type FeedbackCategory,
  type FeedbackSeverity,
} from './constants';
import { readCorner, writeCorner } from './panelPosition';
import type { DraftImage } from './types';

export type PanelView = 'closed' | 'expanded' | 'minimized' | 'picking';

export interface SaveError {
  description: string;
  /** `forbidden` removes Send (the user lost the permission). */
  kind: 'network' | 'validation' | 'forbidden' | 'rate-limit';
}

export interface FocusRequest {
  /** `data-testid` of the element to focus; falls back to `document.body` when it is gone. */
  id: string;
  n: number;
  /** Only move focus when it is currently on `body` (restore after a modal closed). */
  onlyIfBody?: boolean;
}

export interface FeedbackState {
  view: PanelView;
  autoMinimized: boolean;
  modalOpen: boolean;
  corner: Corner;
  note: string;
  category: FeedbackCategory;
  severity: FeedbackSeverity;
  capture: CaptureResult | null;
  images: DraftImage[];
  sending: boolean;
  /** 0 to 100 while sending, otherwise null. */
  progress: number | null;
  saveError: SaveError | null;
  /** A feedback route answered 404 while the panel was open (ADR-14). */
  unavailable: boolean;
  announcement: string;
  focusRequest: FocusRequest | null;
}

const initialDraft = {
  note: '',
  category: 'bug' as FeedbackCategory,
  severity: 'medium' as FeedbackSeverity,
  capture: null as CaptureResult | null,
  images: [] as DraftImage[],
};

function initialState(): FeedbackState {
  return {
    view: 'closed',
    autoMinimized: false,
    modalOpen: false,
    corner: DEFAULT_CORNER,
    ...initialDraft,
    sending: false,
    progress: null,
    saveError: null,
    unavailable: false,
    announcement: '',
    focusRequest: null,
  };
}

let state: FeedbackState = initialState();
let cornerHydrated = false;
let focusCounter = 0;
const listeners = new Set<() => void>();

export const getFeedbackState = (): FeedbackState => state;

export function subscribeFeedback(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function set(patch: Partial<FeedbackState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useFeedbackState(): FeedbackState {
  return useSyncExternalStore(subscribeFeedback, getFeedbackState, getFeedbackState);
}

/** A non-empty draft: note text, any image, or a selection. Category and severity never count (spec 1.2). */
export function hasDraft(s: FeedbackState): boolean {
  return s.note.trim().length > 0 || s.images.length > 0 || s.capture !== null;
}

// ─── Focus and announcements ───────────────────────────────────────────────

export function requestFocus(id: string, onlyIfBody = false): void {
  focusCounter += 1;
  set({ focusRequest: { id, n: focusCounter, onlyIfBody } });
}

export function announce(message: string): void {
  set({ announcement: message });
}

// ─── View transitions ──────────────────────────────────────────────────────

/** Reads the persisted corner once, on first use. */
export function hydrateCorner(): void {
  if (cornerHydrated) return;
  cornerHydrated = true;
  set({ corner: readCorner() });
}

/** The flag was activated (spec section 1.4). */
export function activateFlag(): void {
  const s = state;
  hydrateCorner();
  if (s.view === 'closed') {
    if (state.modalOpen) {
      set({ view: 'minimized', autoMinimized: true, unavailable: false });
      return;
    }
    set({ view: 'expanded', unavailable: false });
    requestFocus('feedback-note');
  } else if (s.view === 'minimized') {
    if (s.modalOpen) return;
    restore();
  } else if (s.view === 'expanded') {
    requestFocus('feedback-note');
  }
}

export function restore(): void {
  if (state.modalOpen) return;
  set({ view: 'expanded', autoMinimized: false });
  requestFocus('feedback-note');
}

export function minimize(): void {
  set({ view: 'minimized', autoMinimized: false });
  requestFocus('feedback-pill');
}

export function beginPick(): void {
  set({ view: 'picking' });
}

export function endPick(): void {
  if (state.view === 'picking') set({ view: 'expanded' });
}

/** A modal dialog appeared or went away (spec section 7.3). */
export function setModalOpen(open: boolean): void {
  const s = state;
  if (s.modalOpen === open) return;
  if (open) {
    if (s.view === 'expanded' || s.view === 'picking') {
      set({ modalOpen: true, view: 'minimized', autoMinimized: true });
    } else {
      set({ modalOpen: true });
    }
    return;
  }
  if (s.view === 'minimized' && s.autoMinimized) {
    set({ modalOpen: false, view: 'expanded', autoMinimized: false });
    requestFocus(lastFocusedId ?? 'feedback-note', true);
  } else {
    set({ modalOpen: false, autoMinimized: false });
  }
}

let lastFocusedId: string | null = null;
export function rememberFocus(id: string | null): void {
  lastFocusedId = id;
}

export function closePanel(): void {
  clearDraft();
  set({ view: 'closed', autoMinimized: false, unavailable: false });
  requestFocus('feedback-flag');
}

// ─── Position ──────────────────────────────────────────────────────────────

export function setCorner(corner: Corner): void {
  writeCorner(corner);
  set({ corner });
}

// ─── Draft ─────────────────────────────────────────────────────────────────

export function setNote(note: string): void {
  set({ note });
}
export function setCategory(category: FeedbackCategory): void {
  set({ category });
}
export function setSeverity(severity: FeedbackSeverity): void {
  set({ severity });
}
export function setCapture(capture: CaptureResult | null): void {
  set({ capture });
}

function revoke(image: DraftImage): void {
  if (image.objectUrl) URL.revokeObjectURL(image.objectUrl);
}

export function addImagePlaceholders(images: DraftImage[]): void {
  set({ images: [...state.images, ...images] });
}

/** Replaces a placeholder with its processed image, or drops it when processing failed. */
export function resolveImage(id: string, image: DraftImage | null): void {
  if (!state.images.some((i) => i.id === id)) {
    if (image) revoke(image);
    return;
  }
  const replacement = image ? [image] : [];
  set({ images: state.images.flatMap((i) => (i.id === id ? replacement : [i])) });
}

export function removeImage(id: string): void {
  const target = state.images.find((i) => i.id === id);
  if (target) revoke(target);
  set({ images: state.images.filter((i) => i.id !== id) });
}

/** Clears the draft and revokes every blob URL. Does not touch the view. */
export function clearDraft(): void {
  state.images.forEach(revoke);
  set({
    ...initialDraft,
    images: [],
    sending: false,
    progress: null,
    saveError: null,
    announcement: '',
  });
}

// ─── Send state ────────────────────────────────────────────────────────────

export function setSending(sending: boolean, progress: number | null = null): void {
  set({ sending, progress });
}
export function setProgress(progress: number | null): void {
  set({ progress });
}
export function setSaveError(saveError: SaveError | null): void {
  set({ saveError });
}
export function setUnavailable(unavailable: boolean): void {
  set({ unavailable, saveError: null });
}

/** Test only: returns the store to its initial state. */
export function resetFeedbackStore(): void {
  state.images.forEach(revoke);
  state = initialState();
  cornerHydrated = false;
  lastFocusedId = null;
  listeners.forEach((l) => l());
}
